import { computed } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'

const PREFIX = '[useAuth]'

// Custom Auth0 claim populated by the Post-Login Action.
// Keep in sync with backend/app/authz.py:ROLES_CLAIM.
export const ROLES_CLAIM = 'https://mercurytrade/roles'

export const ROLE_ADMIN = 'admin'
export const ROLE_INVESTOR = 'investor'

// Guards against multiple in-flight requests each firing a login redirect when
// silent token acquisition hard-fails. Module scope so it's shared across every
// useAuth() consumer until the navigation actually unloads the page.
let _redirecting = false

export function useAuth() {
  const { user, isAuthenticated, isLoading, loginWithRedirect, logout: auth0Logout, getAccessTokenSilently } = useAuth0()

  const roles = computed(() => {
    const claim = user.value?.[ROLES_CLAIM]
    if (!Array.isArray(claim)) return []
    return claim.filter(r => typeof r === 'string').map(r => r.toLowerCase())
  })
  const isAdmin = computed(() => roles.value.includes(ROLE_ADMIN))
  const isInvestor = computed(() => roles.value.includes(ROLE_INVESTOR))
  const hasAnyRole = computed(() => isAdmin.value || isInvestor.value)

  function login() {
    console.log(`${PREFIX} login() called`)
    return loginWithRedirect()
  }

  function logout() {
    console.log(`${PREFIX} logout() called`)
    return auth0Logout({ logoutParams: { returnTo: window.location.origin } })
  }

  async function getAccessToken() {
    console.log(`${PREFIX} getAccessToken() called`)
    try {
      const token = await getAccessTokenSilently()
      console.log(`${PREFIX} getAccessToken() succeeded`)
      return token
    } catch (err) {
      // The SSO session is unrecoverable silently (even the iframe fallback
      // failed): send the user through a full login, preserving where they
      // were so the post-callback redirect lands them back here.
      const code = err?.error
      const unrecoverable =
        code === 'login_required' ||
        code === 'consent_required' ||
        code === 'missing_refresh_token'
      if (unrecoverable && !_redirecting) {
        _redirecting = true
        const target = window.location.pathname + window.location.search
        console.warn(`${PREFIX} session unrecoverable (${code}) — redirecting to login`)
        loginWithRedirect({ appState: { target } })
      }
      const message = `${PREFIX} getAccessToken() failed: ${err?.message ?? 'token unavailable'}`
      console.error(message, err)
      throw new Error(message)
    }
  }

  return {
    user,
    isAuthenticated,
    isLoading,
    roles,
    isAdmin,
    isInvestor,
    hasAnyRole,
    login,
    logout,
    getAccessToken,
  }
}
